import { useCallback, useEffect, useRef, useState } from "react";
import { defaultScreenConfig, type Orientation, type ScreenConfig } from "@signage/shared";
import { apiGet, screenStream } from "@/lib/api";
import { readCachedConfig, writeCachedConfig } from "@/lib/localCache";

export type PlayerConfig = ScreenConfig & { orientation: Orientation };

/**
 * Loads a screen's config with an offline-first strategy: render the last
 * cached config immediately (zero-flash boot even with no network), then
 * fetch live and listen on the screen's event stream for instant pushes.
 * The server includes orientation with every config, so a push is complete.
 */
export function useScreenConfig(screenId: string) {
  const numericId = Number(screenId);
  const cached = readCachedConfig(screenId) as PlayerConfig | null;
  const [config, setConfig] = useState<PlayerConfig | null>(cached);
  const [loading, setLoading] = useState(!cached);
  const orientationRef = useRef<Orientation>(cached?.orientation ?? "landscape");

  const applyConfig = useCallback(
    (next: PlayerConfig) => {
      orientationRef.current = next.orientation ?? orientationRef.current;
      setConfig({ ...next, orientation: orientationRef.current });
      writeCachedConfig(screenId, next as unknown as ScreenConfig);
    },
    [screenId],
  );

  const fetchConfig = useCallback(async (): Promise<boolean> => {
    try {
      const cfg = await apiGet<Partial<PlayerConfig>>(`/screens/${numericId}/config`);
      if (!cfg || cfg.screen_id === undefined) {
        applyConfig({
          ...defaultScreenConfig(numericId),
          updated_at: new Date().toISOString(),
          orientation: cfg?.orientation ?? orientationRef.current,
        });
      } else {
        applyConfig(cfg as PlayerConfig);
      }
      setLoading(false);
      return true;
    } catch (err) {
      console.error("Config fetch failed:", err);
      return false;
    }
  }, [numericId, applyConfig]);

  useEffect(() => {
    let cancelled = false;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;

    async function loadWithRetry() {
      const ok = await fetchConfig();
      if (!ok && !cancelled) {
        retryTimeout = setTimeout(loadWithRetry, 5000);
      }
    }
    loadWithRetry();

    const stream = screenStream(screenId);
    const offConfig = stream.on("config", (data) => {
      const next = data as PlayerConfig;
      if (next && next.screen_id === numericId) applyConfig(next);
    });
    // On every reconnect, re-fetch: a change made while the stream was down
    // would otherwise be missed until the 10-minute watchdog.
    const offOpen = stream.onOpen(() => void fetchConfig());
    stream.acquire();

    return () => {
      cancelled = true;
      if (retryTimeout) clearTimeout(retryTimeout);
      offConfig();
      offOpen();
      stream.release();
    };
  }, [numericId, screenId, fetchConfig, applyConfig]);

  return { config, loading, refetch: fetchConfig };
}
